import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { GetRequest, PostRequest, PutRequest } from "../../services/ApiClientService"
import { manualExternalOrderMemory } from "../../Objects/Memory/Memory"
import { ManualExternalOrder } from "../../Objects/ManualExternalOrder/ManualExternalOrder"
import { ManualExternalOrderFinancialSummary } from "../../Objects/ManualExternalOrder/ManualExternalOrderFinancialSummary"
import { ManualExternalOrderFilter } from "../../Objects/ManualExternalOrder/ManualExternalOrderFilter"

const appendOptional = (params: URLSearchParams, key: string, value?: string | number | null) => {
  if (value === undefined || value === null || value === "") {
    return
  }

  params.append(key, value.toString())
}

export const useManualExternalOrders = () => {
  const { t } = useTranslation("general")

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const buildQuery = (filter: ManualExternalOrderFilter) => {
    const params = new URLSearchParams()

    appendOptional(params, "dateStart", filter.dateStart)
    appendOptional(params, "dateEnd", filter.dateEnd)
    appendOptional(params, "sourceType", filter.sourceType)
    appendOptional(params, "status", filter.status)
    appendOptional(params, "reconciliationStatus", filter.reconciliationStatus)
    appendOptional(params, "paymentMethod", filter.paymentMethod)
    appendOptional(params, "deliveryType", filter.deliveryType)
    appendOptional(params, "minAmount", filter.minAmount)
    appendOptional(params, "maxAmount", filter.maxAmount)
    appendOptional(params, "search", filter.search)
    return params.toString()
  }

  const handleGetManualExternalOrders = async (
    filter: ManualExternalOrderFilter,
    onSuccess: (data: Array<ManualExternalOrder>) => void,
  ) => {
    try {
      enableLoad()

      const response = await GetRequest<Array<ManualExternalOrder>>(
        `${API_ROUTES.MANUAL_EXTERNAL_ORDER.GET_ALL}?${buildQuery(filter)}`,
      )

      if (response.success && response.data) {
        const temp = response.data.map((item) => ({ ...item, items: item.items ?? [] }))

        manualExternalOrderMemory.setList(temp)

        onSuccess(temp)

        return
      }

      throw new Error(response.message)
    } catch (e) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleGetManualExternalOrder = async (id: number, onSuccess: (data: ManualExternalOrder) => void) => {
    try {
      enableLoad()

      const response = await GetRequest<ManualExternalOrder>(API_ROUTES.MANUAL_EXTERNAL_ORDER.GET(id))

      if (response.success && response.data) {
        onSuccess(response.data)

        return
      }

      throw new Error(response.message)
    } catch (e) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleSaveManualExternalOrder = async (order: ManualExternalOrder, onSuccess: () => void) => {
    try {
      enableLoad()

      const response =
        order.id > 0
          ? await PutRequest<ManualExternalOrder>(API_ROUTES.MANUAL_EXTERNAL_ORDER.UPDATE(order.id), order)
          : await PostRequest<ManualExternalOrder>(API_ROUTES.MANUAL_EXTERNAL_ORDER.CREATE, order)

      if (response.success) {
        onSuccess()
        return
      }

      throw new Error(response.message)
    } catch (e) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleCancelManualExternalOrder = async (id: number, onSuccess: () => void) => {
    try {
      enableLoad()

      const response = await PostRequest<ManualExternalOrder>(API_ROUTES.MANUAL_EXTERNAL_ORDER.CANCEL(id), {})

      if (response.success) {
        onSuccess()
        return
      }

      throw new Error(response.message)
    } catch (e) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleGetManualExternalOrderFinancialSummary = async (
    filter: ManualExternalOrderFilter,
    onSuccess: (data: ManualExternalOrderFinancialSummary) => void,
  ) => {
    try {
      const response = await GetRequest<ManualExternalOrderFinancialSummary>(
        `${API_ROUTES.MANUAL_EXTERNAL_ORDER.FINANCIAL_SUMMARY}?${buildQuery(filter)}`,
      )

      if (response.success && response.data) {
        onSuccess(response.data)

        return
      }

      throw new Error(response.message)
    } catch (e) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    }
  }

  return {
    handleGetManualExternalOrders,
    handleGetManualExternalOrder,
    handleSaveManualExternalOrder,
    handleCancelManualExternalOrder,
    handleGetManualExternalOrderFinancialSummary,
  }
}
